/**
 * HeaderInstrumentBlock.tsx — MER Algo Footprints v9.2.0
 *
 * Phase 2: Global Trading Header — Row 2 left edge (Decision Context Bar).
 *
 * Renders the active instrument's identity and live price:
 *   Symbol (28px bold)   Price (22px)   Change% (13px tinted)
 *
 * Pure presentational. All values come from useGlobalTradingContext.
 *
 * Empty-state contract (Phase 2 spec Step 9):
 *   symbol = '' → "NO SYMBOL"
 *   price  = null → "Loading..." while loading, "—" otherwise
 *   change = null → omitted (not rendered)
 */

import React from 'react';

export interface HeaderInstrumentBlockProps {
  symbol:    string;
  price:     number | null;
  changePct: number | null;
  loading:   boolean;
  error?:    string | null;
}

function formatPrice(p: number): string {
  if (p >= 1000) return p.toFixed(2);
  if (p >= 1)    return p.toFixed(2);
  return p.toFixed(4);
}

function formatChange(c: number): string {
  const sign = c > 0 ? '+' : '';
  return `${sign}${c.toFixed(2)}%`;
}

export function HeaderInstrumentBlock({
  symbol,
  price,
  changePct,
  loading,
  error,
}: HeaderInstrumentBlockProps) {
  const symbolDisplay = symbol || 'NO SYMBOL';
  const symbolColor = symbol ? 'var(--mer-yellow)' : 'var(--mer-muted)';

  let priceDisplay: string;
  if (price != null) {
    priceDisplay = formatPrice(price);
  } else if (error) {
    priceDisplay = '—';
  } else if (loading) {
    priceDisplay = 'Loading…';
  } else {
    priceDisplay = '—';
  }

  const changeColor =
    changePct == null ? 'var(--mer-muted)'
      : changePct > 0 ? 'var(--mer-green)'
      : changePct < 0 ? 'var(--mer-red)'
      : 'var(--mer-muted)';

  return (
    <div
      className="mer-instrument-block"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 2,
        padding: '0 18px',
        borderRight: '1px solid var(--mer-border)',
        height: '100%',
        minWidth: 200,
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: symbolColor,
            letterSpacing: 1.2,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            fontFamily: "'Courier New', monospace",
          }}
        >
          {symbolDisplay}
        </span>
        <span
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: 'var(--mer-text)',
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {priceDisplay}
        </span>
      </div>
      {changePct != null && (
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: changeColor,
            lineHeight: 1.2,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatChange(changePct)}
        </span>
      )}
      {changePct == null && loading && (
        <span style={{ fontSize: 11, color: 'var(--mer-muted)' }}>Loading…</span>
      )}
    </div>
  );
}
